import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Radio,
  Phone,
  PhoneOff,
  Eye,
  EyeOff,
  Send,
  MessageSquare,
  Users,
  Clock,
  Wifi,
  WifiOff,
  Volume2,
  LogOut,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import useCallMonitorWS from "../../hooks/useCallMonitorWS";
import { fmtTimeEST } from "../../utils/dateUtils";
import BargeInWebRTC from "../../components/BargeInWebRTC";
import { useAppMessage } from "../../components/AppMessage";

axios.defaults.withCredentials = true;
const API = process.env.REACT_APP_BACKEND_URL;

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatTime(isoStr) {
  return fmtTimeEST(isoStr);
}

const statusColors = {
  dialing: "bg-yellow-100 text-yellow-800",
  ringing: "bg-blue-100 text-blue-800",
  answered: "bg-green-100 text-green-800",
  completed: "bg-gray-100 text-gray-800",
  no_answer: "bg-red-100 text-red-800",
  busy: "bg-orange-100 text-orange-800",
  failed: "bg-red-100 text-red-800",
};

export default function CallMonitorPage() {
  const msg = useAppMessage();
  const {
    connected,
    transcripts,
    callStatuses,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    sendWhisper,
    setActiveCalls: setWsActiveCalls,
  } = useCallMonitorWS();

  const [activeCalls, setActiveCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [selectedCallDetail, setSelectedCallDetail] = useState(null);
  const [whisperText, setWhisperText] = useState("");
  const [whisperHistory, setWhisperHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [bargeData, setBargeData] = useState(null); // {conferenceId, conferenceName, callControlId}
  const transcriptEndRef = useRef(null);
  const pollInterval = useRef(null);
  const durationInterval = useRef(null);
  const [liveDurations, setLiveDurations] = useState({});

  // Connect WebSocket on mount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  // Fetch active calls
  const fetchActiveCalls = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/call-monitor/active-calls`);
      setActiveCalls(res.data.calls || []);
      setWsActiveCalls(res.data.calls || []);
    } catch (err) {
      console.error("Failed to fetch active calls:", err);
    } finally {
      setLoading(false);
    }
  }, [setWsActiveCalls]);

  // Poll active calls every 5 seconds
  useEffect(() => {
    fetchActiveCalls();
    pollInterval.current = setInterval(fetchActiveCalls, 5000);
    return () => clearInterval(pollInterval.current);
  }, [fetchActiveCalls]);

  // Update live durations every second
  useEffect(() => {
    durationInterval.current = setInterval(() => {
      setLiveDurations((prev) => {
        const next = {};
        activeCalls.forEach((call) => {
          if (call.started_at && !callStatuses[call.attempt_id]?.ended) {
            const start = new Date(call.started_at).getTime();
            next[call.attempt_id] = (Date.now() - start) / 1000;
          }
        });
        return next;
      });
    }, 1000);
    return () => clearInterval(durationInterval.current);
  }, [activeCalls, callStatuses]);

  // Remove ended calls from the list
  useEffect(() => {
    Object.entries(callStatuses).forEach(([attemptId, status]) => {
      if (status.ended) {
        setActiveCalls((prev) => prev.filter((c) => c.attempt_id !== attemptId));
        if (selectedCall === attemptId) {
          toast.info("Call has ended");
        }
      }
    });
  }, [callStatuses, selectedCall]);

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcripts, selectedCall]);

  // Monitor a call
  const handleMonitor = async (attemptId) => {
    try {
      const res = await axios.get(`${API}/api/call-monitor/call/${attemptId}`);
      setSelectedCall(attemptId);
      setSelectedCallDetail(res.data);
      setIsMonitoring(true);
      subscribe(attemptId);
      toast.success("Monitoring call");
    } catch (err) {
      toast.error("Failed to start monitoring");
    }
  };

  // Stop monitoring — also clean up active barge if any
  const handleStopMonitor = async () => {
    if (bargeData && selectedCall) {
      try {
        await axios.post(`${API}/api/call-monitor/call/${selectedCall}/unbarge`);
      } catch (err) {
        /* best-effort cleanup */
      }
      setBargeData(null);
    }
    if (selectedCall) {
      unsubscribe(selectedCall);
    }
    setSelectedCall(null);
    setSelectedCallDetail(null);
    setIsMonitoring(false);
    setWhisperText("");
    setWhisperHistory([]);
  };

  // Hang up a call
  const handleHangup = async (attemptId) => {
    const ok = await msg.confirm("End this call?", "The active call will be hung up immediately.", { destructive: true, confirmLabel: "End call" });
    if (!ok) return;
    try {
      await axios.post(`${API}/api/call-monitor/call/${attemptId}/hangup`);
      toast.success("Call ended");
      fetchActiveCalls();
    } catch (err) {
      toast.error("Failed to hang up call");
    }
  };

  // Send whisper
  const handleWhisper = async () => {
    if (!whisperText.trim() || !selectedCall) return;
    try {
      await axios.post(`${API}/api/call-monitor/call/${selectedCall}/whisper`, {
        message: whisperText.trim(),
      });
      setWhisperHistory((prev) => [
        ...prev,
        { text: whisperText.trim(), sent_at: new Date().toISOString(), applied: true },
      ]);
      setWhisperText("");
      toast.success("Whisper sent to AI");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send whisper");
    }
  };

  // Barge in
  const handleBargeIn = async () => {
    if (!selectedCall) return;
    const ok = await msg.confirm("Barge into call?", "Barge in will stop the AI and connect you to the call. Continue?", { confirmLabel: "Barge in" });
    if (!ok) return;
    try {
      const res = await axios.post(`${API}/api/call-monitor/call/${selectedCall}/barge`);
      setBargeData({
        conferenceId: res.data.conference_id,
        conferenceName: res.data.conference_name,
        callControlId: res.data.call_control_id,
      });
      toast.success("Connecting to call...");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to barge in");
    }
  };

  // Get transcript for selected call (combine initial + live)
  const getTranscript = () => {
    const initial = selectedCallDetail?.transcript || [];
    const live = transcripts[selectedCall] || [];

    // Merge: start with initial, add live messages that aren't duplicates
    const merged = [...initial];
    live.forEach((liveMsg) => {
      const isDup = merged.some(
        (m) => m.text === liveMsg.text && m.role === liveMsg.role
      );
      if (!isDup) {
        merged.push(liveMsg);
      }
    });
    return merged;
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Radio className="h-6 w-6 text-red-500" />
          <h1 className="text-2xl font-bold">Live Call Monitor</h1>
          <Badge variant={connected ? "default" : "destructive"} className="ml-2">
            {connected ? (
              <><Wifi className="h-3 w-3 mr-1" /> Connected</>
            ) : (
              <><WifiOff className="h-3 w-3 mr-1" /> Disconnected</>
            )}
          </Badge>
        </div>
        <Button variant="outline" size="sm" onClick={fetchActiveCalls}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Active Calls Panel */}
        <div className={isMonitoring ? "lg:col-span-1" : "lg:col-span-3"}>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Phone className="h-5 w-5" />
                Active Calls
                <Badge variant="secondary" className="ml-auto">
                  {activeCalls.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : activeCalls.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Phone className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>No active calls</p>
                  <p className="text-sm mt-1">Calls will appear here in real-time when campaigns are running</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {activeCalls.map((call) => (
                    <div
                      key={call.attempt_id}
                      className={`p-3 border rounded-lg transition-colors cursor-pointer hover:bg-slate-50 ${
                        selectedCall === call.attempt_id ? "ring-2 ring-primary bg-primary/5" : ""
                      }`}
                      onClick={() => !isMonitoring && handleMonitor(call.attempt_id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm truncate">{call.lead_name}</span>
                        <Badge className={`text-xs ${statusColors[call.status] || "bg-gray-100"}`}>
                          {call.status}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{call.campaign_name || "Direct Call"}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDuration(liveDurations[call.attempt_id] || call.duration_seconds)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-muted-foreground">{call.to_number}</span>
                        <div className="flex gap-1">
                          {selectedCall !== call.attempt_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMonitor(call.attempt_id);
                              }}
                            >
                              <Eye className="h-3 w-3 mr-1" /> Monitor
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHangup(call.attempt_id);
                            }}
                          >
                            <PhoneOff className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {call.is_monitored && (
                        <div className="mt-1 flex items-center gap-1 text-xs text-blue-600">
                          <Users className="h-3 w-3" />
                          {call.monitoring_supervisors?.length || 0} supervisor(s) watching
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monitoring Panel - only shown when actively monitoring */}
        {isMonitoring && selectedCall && (
          <div className="lg:col-span-2 space-y-4">
            {/* Call Info Bar */}
            <Card>
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                      <span className="font-semibold">
                        Monitoring: {selectedCallDetail?.lead_name || "Unknown"}
                      </span>
                      <Badge className={statusColors[callStatuses[selectedCall]?.status || selectedCallDetail?.status] || "bg-gray-100"}>
                        {callStatuses[selectedCall]?.status || selectedCallDetail?.status}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {selectedCallDetail?.to_number} | {selectedCallDetail?.campaign_name || "Direct"} |{" "}
                      <Clock className="h-3 w-3 inline" />{" "}
                      {formatDuration(liveDurations[selectedCall] || selectedCallDetail?.duration_seconds)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleBargeIn}
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    >
                      <Volume2 className="h-4 w-4 mr-1" /> Barge In
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleHangup(selectedCall)}
                    >
                      <PhoneOff className="h-4 w-4 mr-1" /> End Call
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleStopMonitor}>
                      <EyeOff className="h-4 w-4 mr-1" /> Leave
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Live Transcript */}
            <Card className="flex-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Live Transcript
                  <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse ml-1" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[350px] pr-4">
                  <div className="space-y-3">
                    {getTranscript().length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-20" />
                        <p>Waiting for conversation...</p>
                      </div>
                    ) : (
                      getTranscript().map((entry, idx) => (
                        <div
                          key={idx}
                          className={`flex gap-3 ${entry.role === "ai" ? "" : "flex-row-reverse"}`}
                        >
                          <div
                            className={`max-w-[80%] p-3 rounded-lg text-sm ${
                              entry.role === "ai"
                                ? "bg-blue-50 border border-blue-100 text-blue-900"
                                : "bg-green-50 border border-green-100 text-green-900"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-xs uppercase">
                                {entry.role === "ai" ? "AI Agent" : "Customer"}
                              </span>
                              <span className="text-xs opacity-60">{formatTime(entry.timestamp)}</span>
                            </div>
                            <p>{entry.text}</p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={transcriptEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Whisper Panel */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  Whisper to AI
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Send a note to the AI agent. It will incorporate your guidance in its next response.
                  The customer will hear a brief pause (~1-2s).
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g. Offer a 10% discount if they hesitate..."
                    value={whisperText}
                    onChange={(e) => setWhisperText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleWhisper()}
                    className="flex-1"
                  />
                  <Button onClick={handleWhisper} disabled={!whisperText.trim()}>
                    <Send className="h-4 w-4 mr-1" /> Send
                  </Button>
                </div>
                {/* Whisper History */}
                {whisperHistory.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Whisper History:</p>
                    {whisperHistory.map((w, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-100 rounded px-2 py-1">
                        <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700">
                          {w.applied ? "Applied" : "Pending"}
                        </Badge>
                        <span className="flex-1 truncate">{w.text}</span>
                        <span className="text-muted-foreground">{formatTime(w.sent_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Barge-In WebRTC Dialog */}
      {bargeData && (
        <BargeInWebRTC
          isOpen={!!bargeData}
          onClose={() => setBargeData(null)}
          conferenceId={bargeData.conferenceId}
          conferenceName={bargeData.conferenceName}
          callControlId={bargeData.callControlId}
          attemptId={selectedCall}
          leadName={selectedCallDetail?.lead_name}
        />
      )}
    </div>
  );
}
